import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Package, Calendar, Hash, Search, Filter, RefreshCw, AlertTriangle,
  CheckCircle2, Clock, TrendingDown, FileText, QrCode, Eye, Edit
} from 'lucide-react';

const InventoryControl = () => {
  const [activeTab, setActiveTab] = useState('lot'); // lot, fefo, cycle
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data for lot tracking
  const lotTrackedItems = [
    {
      id: 1,
      sku: 'PHARM-001',
      name: 'Medicine XYZ 100mg',
      lot: 'LOT-2024-001',
      quantity: 500,
      location: 'Zone C → Aisle C1 → Rack C1-R1 → Shelf C1-R1-S1 → Bin C1-R1-S1-B1',
      receivedDate: '2024-01-15',
      expiryDate: '2025-01-15',
      supplier: 'Pharma Corp',
      status: 'active',
      serials: ['SN-001', 'SN-002', 'SN-003'] // Sample serials
    },
    {
      id: 2,
      sku: 'ELEC-001',
      name: 'Laptop Dell XPS 15',
      lot: 'LOT-2024-045',
      quantity: 50,
      location: 'Zone A → Aisle A1 → Rack A1-R1 → Shelf A1-R1-S1 → Bin A1-R1-S1-B1',
      receivedDate: '2024-01-20',
      expiryDate: null,
      supplier: 'Tech Solutions',
      status: 'active',
      serials: ['SN-101', 'SN-102', 'SN-103']
    },
    {
      id: 3,
      sku: 'FOOD-001',
      name: 'Canned Goods Premium',
      lot: 'LOT-2024-089',
      quantity: 1000,
      location: 'Zone C → Aisle C2 → Rack C2-R1 → Shelf C2-R1-S1 → Bin C2-R1-S1-B1',
      receivedDate: '2024-01-10',
      expiryDate: '2024-06-10',
      supplier: 'Food Distributors',
      status: 'expiring-soon',
      serials: []
    }
  ];

  // Mock data for FEFO (First Expiry, First Out)
  const fefoItems = [
    {
      id: 1,
      sku: 'PHARM-001',
      name: 'Medicine XYZ 100mg',
      lots: [
        { lot: 'LOT-2024-001', quantity: 200, expiryDate: '2025-01-15', daysUntilExpiry: 352, priority: 'low' },
        { lot: 'LOT-2024-002', quantity: 300, expiryDate: '2024-12-15', daysUntilExpiry: 322, priority: 'medium' },
        { lot: 'LOT-2024-003', quantity: 150, expiryDate: '2024-11-15', daysUntilExpiry: 291, priority: 'high' }
      ],
      totalQuantity: 650,
      suggestedPickLot: 'LOT-2024-003',
      location: 'Zone C → Aisle C1'
    },
    {
      id: 2,
      sku: 'FOOD-001',
      name: 'Canned Goods Premium',
      lots: [
        { lot: 'LOT-2024-089', quantity: 500, expiryDate: '2024-06-10', daysUntilExpiry: 134, priority: 'high' },
        { lot: 'LOT-2024-090', quantity: 300, expiryDate: '2024-08-10', daysUntilExpiry: 195, priority: 'medium' },
        { lot: 'LOT-2024-091', quantity: 200, expiryDate: '2024-10-10', daysUntilExpiry: 256, priority: 'low' }
      ],
      totalQuantity: 1000,
      suggestedPickLot: 'LOT-2024-089',
      location: 'Zone C → Aisle C2'
    }
  ];

  // Mock data for cycle counting
  const cycleCountTasks = [
    {
      id: 1,
      type: 'ABC Analysis',
      zone: 'Zone A',
      location: 'Zone A → Aisle A1 → Rack A1-R1',
      items: [
        { sku: 'ELEC-001', name: 'Laptop Dell XPS', expected: 50, counted: 0, variance: 0, status: 'pending' },
        { sku: 'ELEC-002', name: 'Monitor 27"', expected: 30, counted: 0, variance: 0, status: 'pending' }
      ],
      assignedTo: 'John Doe',
      dueDate: '2024-01-29',
      status: 'assigned',
      priority: 'high'
    },
    {
      id: 2,
      type: 'Location-Based',
      zone: 'Zone B',
      location: 'Zone B → Aisle B1 → Rack B1-R1',
      items: [
        { sku: 'FURN-045', name: 'Office Chair', expected: 89, counted: 0, variance: 0, status: 'pending' }
      ],
      assignedTo: 'Jane Smith',
      dueDate: '2024-01-30',
      status: 'assigned',
      priority: 'medium'
    },
    {
      id: 3,
      type: 'Random Sample',
      zone: 'Zone C',
      location: 'Zone C → Aisle C1 → Rack C1-R1',
      items: [
        { sku: 'PHARM-001', name: 'Medicine XYZ', expected: 500, counted: 498, variance: -2, status: 'completed' }
      ],
      assignedTo: 'Mike Wilson',
      dueDate: '2024-01-28',
      status: 'completed',
      priority: 'low'
    }
  ];

  const getExpiryStatus = (daysUntilExpiry) => {
    if (daysUntilExpiry < 30) return { color: 'red', label: 'Critical' };
    if (daysUntilExpiry < 90) return { color: 'orange', label: 'Warning' };
    if (daysUntilExpiry < 180) return { color: 'yellow', label: 'Caution' };
    return { color: 'green', label: 'Good' };
  };

  const renderLotTracking = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Lot & Serial Tracking</h3>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by SKU, lot, or serial..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm">
                <Filter className="w-4 h-4" />
                Filter
              </button>
            </div>
          </div>
          <div className="space-y-4">
            {lotTrackedItems.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{item.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">SKU: {item.sku}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    item.status === 'active' ? 'bg-green-100 text-green-700' :
                    item.status === 'expiring-soon' ? 'bg-orange-100 text-orange-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {item.status}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Hash className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-600">Lot Number</span>
                    </div>
                    <p className="font-semibold text-gray-900">{item.lot}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Package className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-600">Quantity</span>
                    </div>
                    <p className="font-semibold text-gray-900">{item.quantity}</p>
                  </div>
                  {item.expiryDate && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-600">Expiry Date</span>
                      </div>
                      <p className="font-semibold text-gray-900">{item.expiryDate}</p>
                    </div>
                  )}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-600">Received</span>
                    </div>
                    <p className="font-semibold text-gray-900">{item.receivedDate}</p>
                  </div>
                </div>
                {item.serials && item.serials.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-600 mb-2">Serial Numbers ({item.serials.length}):</p>
                    <div className="flex flex-wrap gap-2">
                      {item.serials.map((serial, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                          {serial}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-600 mb-1">Location:</p>
                  <p className="text-sm text-gray-900">{item.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderFEFO = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">FEFO (First Expiry, First Out)</h3>
              <p className="text-sm text-gray-600 mt-1">Automatic lot selection based on expiry dates</p>
            </div>
            <button
              onClick={() => {
                alert('Refreshing FEFO analysis... This will recalculate lot priorities based on expiry dates.');
              }}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh Analysis
            </button>
          </div>
          <div className="space-y-6">
            {fefoItems.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 border border-gray-200 rounded-lg"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">{item.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">SKU: {item.sku}</p>
                    <p className="text-sm text-gray-700 mt-1">Total Quantity: {item.totalQuantity} units</p>
                  </div>
                  <div className="text-right">
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      Suggested: {item.suggestedPickLot}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  {item.lots.map((lot, index) => {
                    const expiryStatus = getExpiryStatus(lot.daysUntilExpiry);
                    return (
                      <div
                        key={index}
                        className={`p-4 border-2 rounded-lg ${
                          lot.lot === item.suggestedPickLot
                            ? 'border-green-300 bg-green-50'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Hash className="w-4 h-4 text-gray-500" />
                              <span className="font-semibold text-gray-900">{lot.lot}</span>
                              {lot.lot === item.suggestedPickLot && (
                                <span className="px-2 py-0.5 bg-green-600 text-white rounded text-xs font-medium">
                                  PICK THIS LOT
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">Quantity:</span>
                                <span className="font-semibold text-gray-900 ml-2">{lot.quantity}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Expiry:</span>
                                <span className="font-semibold text-gray-900 ml-2">{lot.expiryDate}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Days Left:</span>
                                <span className={`font-semibold ml-2 ${
                                  expiryStatus.color === 'red' ? 'text-red-600' :
                                  expiryStatus.color === 'orange' ? 'text-orange-600' :
                                  expiryStatus.color === 'yellow' ? 'text-yellow-600' :
                                  'text-green-600'
                                }`}>
                                  {lot.daysUntilExpiry}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              lot.priority === 'high' ? 'bg-red-100 text-red-700' :
                              lot.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {lot.priority} priority
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-600 mb-1">Location: {item.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderCycleCounting = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Cycle Counting</h3>
              <p className="text-sm text-gray-600 mt-1">Continuous inventory verification without shutdown</p>
            </div>
            <button className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 text-sm">
              <RefreshCw className="w-4 h-4" />
              Generate Tasks
            </button>
          </div>
          <div className="space-y-4">
            {cycleCountTasks.map((task) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-6 border-2 rounded-lg ${
                  task.status === 'completed' ? 'border-green-200 bg-green-50' :
                  task.status === 'assigned' ? 'border-blue-200 bg-blue-50' :
                  'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">Task #{task.id} - {task.type}</h4>
                    <p className="text-sm text-gray-600 mt-1">Location: {task.location}</p>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-600">{task.items.length} items</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-600">Due: {task.dueDate}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">Assigned: {task.assignedTo}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      task.status === 'completed' ? 'bg-green-100 text-green-700' :
                      task.status === 'assigned' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {task.status}
                    </span>
                    <span className={`mt-2 block px-3 py-1 rounded-full text-xs font-medium ${
                      task.priority === 'high' ? 'bg-red-100 text-red-700' :
                      task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {task.priority} priority
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  {task.items.map((item, index) => (
                    <div
                      key={index}
                      className="p-3 bg-white rounded border border-gray-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-sm text-gray-600">SKU: {item.sku}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <span className="text-xs text-gray-600">Expected:</span>
                            <span className="font-semibold text-gray-900 ml-2">{item.expected}</span>
                          </div>
                          {task.status === 'completed' && (
                            <>
                              <div className="text-right">
                                <span className="text-xs text-gray-600">Counted:</span>
                                <span className={`font-semibold ml-2 ${
                                  item.variance === 0 ? 'text-green-600' :
                                  item.variance > 0 ? 'text-blue-600' :
                                  'text-red-600'
                                }`}>
                                  {item.counted}
                                </span>
                              </div>
                              <div className="text-right">
                                <span className="text-xs text-gray-600">Variance:</span>
                                <span className={`font-semibold ml-2 ${
                                  item.variance === 0 ? 'text-green-600' :
                                  item.variance > 0 ? 'text-blue-600' :
                                  'text-red-600'
                                }`}>
                                  {item.variance > 0 ? '+' : ''}{item.variance}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      {task.status === 'assigned' && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <button
                            onClick={() => {
                              alert(`Starting cycle count for task #${task.id}. This would open a counting interface in production.`);
                            }}
                            className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2 text-sm font-medium"
                          >
                            <QrCode className="w-4 h-4" />
                            Start Counting
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Inventory Control & Traceability</h2>
            <p className="text-sm text-gray-600 mt-1">Advanced tracking: Lot/Serial, FEFO, and Cycle Counting</p>
          </div>
        </div>
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
          {[
            { id: 'lot', label: 'Lot & Serial Tracking', icon: Hash },
            { id: 'fefo', label: 'FEFO Management', icon: TrendingDown },
            { id: 'cycle', label: 'Cycle Counting', icon: RefreshCw }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-orange-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'lot' && renderLotTracking()}
      {activeTab === 'fefo' && renderFEFO()}
      {activeTab === 'cycle' && renderCycleCounting()}
    </div>
  );
};

export default InventoryControl;
